<template>
  <div class="space-y-4">
    <!-- Header with Search and Download All -->
    <div class="flex flex-wrap items-center justify-between gap-4">
      <div class="p-input-icon-left w-full sm:w-auto">
        <IconField iconPosition="left" class="w-full">
        <InputIcon class="pi pi-search text-gray-400" />
        <InputText v-model="searchQuery" placeholder="Search student test..." class="w-full sm:w-80" />
      </IconField>
      </div>
      <div class="flex gap-2">
        <Button label="Download All Participants" icon="pi pi-download" size="small"
                @click="downloadAllResults" :loading="isDownloadingAll" data-testid="btn-cbt-download-all" />
        <Button label="Download Theory Answers" icon="pi pi-file-export" size="small"
                severity="secondary" @click="downloadTheoryAnswersForTest" :loading="isDownloadingTheory" data-testid="btn-cbt-download-theory" />
        <Button
          v-if="canShowMonitoringButton"
          label="View Recordings"
          icon="pi pi-video"
          size="small"
          severity="info"
          @click="openAllRecordings"
          data-testid="btn-cbt-view-recordings"
        />
      </div>
    </div>
    <!-- Loading Spinner -->
    <div v-if="testResults.loading.value" class="flex justify-center py-10">
      <span class="pi pi-spin pi-spinner text-3xl text-primary"></span>
    </div>

    <!-- The request failed: say so instead of "No results available" -->
    <LoadErrorMessage v-else-if="testResults.error.value" what="the results" :error="testResults.error.value"
      @retry="testResults.refetch()" />

    <!-- Table -->
    <ReusablePrimeTable v-else :headers="headers" :data="tableRows" :paginator="true" :lazy="true" :rows="rows"
      :rowsPerPageOptions="rowsPerPageOptions" :totalRecords="paginatorInfo?.total || 0"
      :first="((paginatorInfo?.currentPage ?? 1) - 1) * rows" @page="onPageChange" emptyMessage="No results available">
      <template #actions="{ row }">
        <div class="flex gap-2">
          <Button label="Grade" size="small" @click="goToGrading(row.id)" data-testid="btn-cbt-grade-student" />
          <Button icon="pi pi-download" size="small" severity="secondary" @click="downloadResult(row.id)"
            v-tooltip="'Download Result'" data-testid="btn-cbt-download-result" />
          <Button icon="pi pi-trash" size="small" severity="danger" @click="deleteSession(row.id)"
            v-tooltip="'Delete Session'" data-testid="btn-cbt-delete-session" />
        </div>
      </template>
    </ReusablePrimeTable>
  </div>
</template>

<script setup lang="ts">
import ReusablePrimeTable from '@/components/common/ReusablePrimeTable.vue';
import LoadErrorMessage from '@/components/common/LoadErrorMessage.vue';
import { useCBTTestResults } from '@/composables/queries/cbt/useCBTTestResults';
import { refDebounced } from '@vueuse/core';
import { routeNames } from '@/constants/route-details.constant';
import { useCbtTestMutations } from '@/composables/mutations/cbt/useCbtTestMutations';
import { useDownloadCbtResultLazyQuery, useDownloadAllCbtTestResultsLazyQuery, useDownloadTheoryAnswersLazyQuery } from '@/types/vue-apollo-helpers';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import { useMonitoringFeatureGate } from '@/features/cbt-monitoring';
import { useUserStore } from '@/store/userStore';
import { AppPermissions, can } from '@/utils/permissions';
import { loadOrRefetch } from '@/utils/lazyQuery';
import { getErrorMessage } from '@/utils/error';

const { enabled: monitoringEnabled } = useMonitoringFeatureGate();
const userStore = useUserStore();
// Both checks needed: feature flag governs whether the school has paid for
// monitoring at all; the permission governs whether THIS user is allowed to
// view recordings within a feature-enabled school.
const canViewMonitoring = computed(() =>
  can(
    userStore.currentUser,
    AppPermissions.CbtMonitoring.Actions.View,
    AppPermissions.CbtMonitoring.Resource,
  ),
);
const canShowMonitoringButton = computed(() => monitoringEnabled.value && canViewMonitoring.value);

const props = defineProps<{
  testId: string;
}>();

const router = useRouter();

const rows = ref(10);
const page = ref(1);
const rowsPerPageOptions = [10, 20, 50, 100];
const searchQuery = ref('');
const debouncedSearchQuery = refDebounced(searchQuery, 500);

const variables = computed(() => ({
  testId: props.testId,
  search: debouncedSearchQuery.value || undefined,
  first: rows.value,
  page: page.value,
}));

const { testResults } = useCBTTestResults({
  autoFetch: true,
  variables,
});

const paginatorInfo = computed(() => testResults.paginatorInfo.value);

function formatStatus(status?: string | null) {
  if (!status) return '--';
  // Example: IN_PROGRESS -> In Progress
  return status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
}

function formatDuration(startTime?: string | null, endTime?: string | null) {
  if (!startTime || !endTime) return '--';
  const diffMs = new Date(endTime).getTime() - new Date(startTime).getTime();
  if (diffMs < 0) return '--';
  const minutes = Math.floor(diffMs / 60000);
  const seconds = Math.floor((diffMs % 60000) / 1000);
  return `${minutes}m ${seconds}s`;
}

function formatDateTime(dateStr?: string | null) {
  if (!dateStr) return '--';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

const headers = [
  { field: 'student', header: 'Student' },
  { field: 'matric_no', header: 'Matric No.' },
  { field: 'score', header: 'Score' },
  { field: 'maxScoreSetOnTest', header: 'Max Score' },
  { field: 'status', header: 'Status' },
  { field: 'startedAt', header: 'Started At' },
  { field: 'duration', header: 'Time Taken' },
  { field: 'actions', header: 'Actions', type: 'actions' },
];

const tableRows = computed(() =>
  (testResults.data.value || []).map((result) => {
    const session = result.cbtSession;
    const score = result.score ?? 0;
    const maxScore = result.maxScoreSetOnTest ?? 0;
    
    return {
      student: `${result.student?.surname ?? ''} ${result.student?.first_name ?? ''}`.trim(),
      matric_no: result.student?.matric_no ?? '--',
      score: score,
      maxScoreSetOnTest: maxScore,
      status: formatStatus(session?.status),
      duration: formatDuration(session?.start_time, session?.end_time),
      startedAt: formatDateTime(session?.start_time),
      id: session?.id, // Session ID for grading
    };
  }),
);

function goToGrading(sessionId: string) {
  if (sessionId) {
    router.push({ name: routeNames.CBT_GRADING.name, params: { sessionId } });
  }
}

function openAllRecordings() {
  router.push({
    name: 'admin-cbt-test-recordings',
    params: { testId: props.testId },
  });
}

const { deleteCBTTestSession } = useCbtTestMutations();
// const cbtStore = useCBTStore();
const toast = useToast();
const confirm = useConfirm();

// Every click must start a new export (or sign a fresh link), so never answer from the cache.
const downloadQuery = useDownloadCbtResultLazyQuery(undefined, { fetchPolicy: 'network-only' });
const downloadAllQuery = useDownloadAllCbtTestResultsLazyQuery(undefined, { fetchPolicy: 'network-only' });
const downloadTheoryQuery = useDownloadTheoryAnswersLazyQuery(undefined, { fetchPolicy: 'network-only' });
const isDownloadingAll = downloadAllQuery.loading;
const isDownloadingTheory = downloadTheoryQuery.loading;

function downloadFailed(error: unknown) {
  toast.add({ severity: 'error', summary: 'Couldn\'t start the download', detail: getErrorMessage(error), life: 6000 });
}

function exportStarted(detail: string) {
  toast.add({ severity: 'success', summary: 'Export Started', detail, life: 5000 });
}

async function downloadResult(sessionId: string) {
  try {
    const result = await loadOrRefetch(downloadQuery, { cbt_test_session_id: sessionId });
    if (result?.downloadCBTResult) window.open(result.downloadCBTResult, '_blank');
  } catch (error) {
    downloadFailed(error);
  }
}

async function downloadAllResults() {
  try {
    const result = await loadOrRefetch(downloadAllQuery, { cbt_test_id: props.testId });
    if (result?.downloadAllCBTTestResults?.id) {
      exportStarted('Your results export is being generated. Check the Downloads icon in the top bar for progress.');
    }
  } catch (error) {
    downloadFailed(error);
  }
}

async function downloadTheoryAnswersForTest() {
  try {
    const result = await loadOrRefetch(downloadTheoryQuery, { cbt_test_id: props.testId });
    if (result?.downloadTheoryAnswers?.id) {
      exportStarted('Your theory answers PDF is being generated. Check the Downloads icon in the top bar for progress.');
    }
  } catch (error) {
    downloadFailed(error);
  }
}

function deleteSession(sessionId: string) {
  confirm.require({
    message: 'Are you sure you want to delete this session? This action cannot be undone.',
    header: 'Delete Session',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    accept: async() => {
      await deleteCBTTestSession(sessionId);
      testResults.refetch();
    },
  });
}

function onPageChange(event: { first: number; rows: number }) {
  const newPage = Math.floor(event.first / event.rows) + 1;
  page.value = newPage;
  rows.value = event.rows;
}
</script>
